import type { IconName } from '../components/Icon'

/**
 * A picture for each kind of work.
 *
 * ── Why the mapping lives here and not in the icon set ─────────────────────
 *
 * `Icon.tsx` is core and ships with every Studio42 app; "hospitality means a
 * bed" is this island's job board talking. The glyphs are general, the meaning
 * is ours.
 *
 * ── Why a picture at all ───────────────────────────────────────────────────
 *
 * The category grid is fourteen choices — the longest single decision in the
 * app — and it is read by someone picking the work they do, often in their
 * second language. A shape is recognised before a word is read, and once you
 * have picked yours the grid becomes something you scan rather than re-read
 * every time you come back to your profile.
 *
 * Every glyph is a TOOL or a PLACE, never a person doing the job: a figure in
 * a chef's hat or a woman with a mop puts a body and a gender on a category
 * anybody can pick.
 */
const ICONS: Record<string, IconName> = {
  hospitality: 'bed',
  food: 'utensils',
  construction: 'hardhat',
  marine: 'ship',
  tours: 'route',
  cleaning: 'spray',
  grounds: 'leaf',
  retail: 'store',
  transport: 'car',
  admin: 'laptop',
  care: 'heart',
  skilled: 'wrench',
  events: 'music',
  other: 'dots',
}

/**
 * The icon for a category id.
 *
 * Falls back to the briefcase, because a category can be added from the admin
 * command line at any time and an unknown id must render as work rather than
 * as a hole in the grid.
 */
export function categoryIcon(id: string): IconName {
  return ICONS[id] ?? 'briefcase'
}
